import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DespesaModal from './components/modals/DespesaModal';
import ExtratoPicker from './components/modals/ExtratoPicker';
import TransferirContaModal from './components/modals/TransferirContaModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import { formatCurrency } from './utils/formatMoeda';
import { fetchExtratos } from './api/extratos';

// Função utilitária para converter string monetária (brasileira ou padrão) para número
function parseMoeda(valor) {
  if (typeof valor === 'number') return valor;
  if (!valor) return 0;
  // Remove pontos de milhar e troca vírgula por ponto
  return parseFloat(String(valor).replace(/\./g, '').replace(',', '.')) || 0;
}
import { formatDateView, toIsoDate } from './utils/dateUtils';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = {
  ano: '', idPrograma: '', idAcao: '', idEscola: '', idConta: '', idFavorecido: '',
  idNatureza: '', idTipoBem: '', docNum: '', docData: '',
  origem: '', docTipo: '', pgForma: '', pgNum: '', pgData: '', vlr: '',
  chaveNf: '' // Campo para a chave da nota fiscal eletrônica
};
// Utilitário para garantir formato ISO (YYYY-MM-DD) para datas
function toIsoDateString(date) {
  if (!date) return '';
  if (date instanceof Date) return date.toISOString().slice(0, 10);
  if (typeof date === 'string' && date.length > 10) return date.slice(0, 10);
  return String(date);
}

function Despesas() {
  // Hooks e variáveis devem vir antes de qualquer lógica
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const [items, setItems] = useState([]);
  const [sortField, setSortField] = useState('docData');
  const [sortDir, setSortDir] = useState('asc');
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [totalExtratos, setTotalExtratos] = useState(null);
  const [showExtratoPicker, setShowExtratoPicker] = useState(false);
  const [extratos, setExtratos] = useState([]);
  const [statusBensPorDespesa, setStatusBensPorDespesa] = useState({});
  const [filtroPrograma, setFiltroPrograma] = useState("");
  const isMaster = JSON.parse(localStorage.getItem('user') || '{}')?.role === 'MASTER';
  const [showTransferirModal, setShowTransferirModal] = useState(false);
  const [transferirTarget, setTransferirTarget] = useState(null);

  // Buscar status dos bens por despesa do backend
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);

  

  // Buscar status dos bens por despesa ao carregar despesas
  useEffect(() => {
    if (!token || !cnpj || !ano) {
      setStatusBensPorDespesa({});
      return;
    }
    axios.get(`${API_BASE_URL.BENS}/statusPorDespesa`, {
      params: { idEscola: cnpj, ano },
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        setStatusBensPorDespesa(res.data || {});
      })
      .catch(() => setStatusBensPorDespesa({}));
  }, [token, cnpj, ano, atualizar]);

  // Buscar extratos e calcular total ao carregar despesas
  useEffect(() => {
    if (!token || !cnpj || !ano) {
      setTotalExtratos(null);
      setExtratos([]);
      return;
    }
    fetchExtratos({ token, cnpj, ano })
      .then(extratos => {
        setExtratos(extratos);
      })
      .catch(() => { setTotalExtratos(null); setExtratos([]); });
  }, [token, cnpj, ano, atualizar]);

  // Recalcula totalExtratos conforme filtroPrograma ou lista de extratos
  useEffect(() => {
    if (!extratos || extratos.length === 0) { setTotalExtratos(null); return; }
    const filtered = filtroPrograma
      ? extratos.filter(e => {
          const pid = (e.idPrograma ?? e.programa ?? e.programaId ?? (e.programa && e.programa.id) ?? '').toString();
          return String(filtroPrograma) === pid || String(filtroPrograma) === (e.programa || '').toString();
        })
      : extratos;
    const total = filtered.reduce((sum, e) => sum + (parseFloat(e.vlr ?? e.valor) || 0), 0);
    setTotalExtratos(total);
  }, [extratos, filtroPrograma]);

  // Função para formatar valores
  function formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  // Feedback de comparação
  function renderFeedback() {
    if (typeof totalDespesas === 'number' && typeof totalExtratos === 'number') {
      if (Math.abs(totalDespesas - totalExtratos) < 0.01) {
        return `✔️ O total das despesas (${formatCurrency(totalDespesas)}) confere com o total dos extratos (${formatCurrency(totalExtratos)}).`;
      } else if (totalDespesas > totalExtratos) {
        return `⚠️ O total das despesas (${formatCurrency(totalDespesas)}) é MAIOR que o total dos extratos (${formatCurrency(totalExtratos)}). Verifique possíveis lançamentos faltantes nos extratos.`;
      } else {
        return `⚠️ O total dos extratos (${formatCurrency(totalExtratos)}) é MAIOR que o total das despesas (${formatCurrency(totalDespesas)}). Verifique possíveis despesas não lançadas.`;
      }
    } else {
      return 'ℹ️ Não foi possível comparar os totais. Verifique se os dados de despesas e extratos estão disponíveis.';
    }
  }

  // ...restante do componente...
  // Exemplo de uso do feedback no JSX:
  // <div style={{ margin: '16px 0', padding: '12px', background: '#f5f5f5', borderRadius: '6px', fontWeight: 'bold' }}>{renderFeedback()}</div>
  const { programas, acoes, contas, favorecidos, naturezas, tiposBens } = useReferenceData(token, cnpj);
  // Exibe a descrição da ação se ela já contém o nome do programa, senão concatena ambos
  const progAcaoLabel = (item) => {
    // Agora as descrições vêm do backend via DTO
    return [item.programaDescricao, item.acaoDescricao].filter(Boolean).join(' ');
  };
  const naturezaLabel = (item) => item.naturezaDescricao || item.idNatureza;
  const favorecidoLabel = id => { const f = favorecidos.find(x => x.cpfOrCnpj === id); return f ? `${f.nome} (${id})` : id; };
  // Natureza K = Capital, C = Custeio
  const isCapital = item => String(item.idNatureza || '').toUpperCase().includes('K');
  // Sort items by `docData` (default: desc) and then paginate
  function getDocTime(item) {
    try {
      const iso = normalizeDate(item.docData);
      // normalizeDate returns '' or 'YYYY-MM-DD' or similar
      if (!iso) return 0;
      // Ensure full timestamp by adding time portion
      return Date.parse(iso);
    } catch {
      return 0;
    }
  }

  const sortedItems = React.useMemo(() => {
    if (!sortField) return items;
    const copy = [...items];
    if (sortField === 'docData') {
      copy.sort((a, b) => {
        const ta = getDocTime(a);
        const tb = getDocTime(b);
        if (ta === tb) return 0;
        return (ta - tb) * (sortDir === 'asc' ? 1 : -1);
      });
    }
    return copy;
  }, [items, sortField, sortDir]);

  const filteredItems = filtroPrograma
    ? sortedItems.filter(item => String(item.idPrograma) === String(filtroPrograma))
    : sortedItems;
  const displayed = filteredItems.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);
  const totalDespesas = filteredItems.reduce((sum, item) => sum + (parseFloat(item.vlr) || 0), 0);

  const resumoPorPrograma = React.useMemo(() => {
    const map = {};
    (Array.isArray(filteredItems) ? filteredItems : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, custeio: 0, capital: 0, qtdC: 0, qtdK: 0 };
      const valor = parseFloat(it.vlr) || 0;
      if (isCapital(it)) {
        map[chave].capital += valor;
        map[chave].qtdK += 1;
      } else {
        map[chave].custeio += valor;
        map[chave].qtdC += 1;
      }
    });
    return Object.values(map);
  }, [filteredItems]);

  useEffect(() => {
    if (!cnpj || !ano) { showDefaultModal('error', 'Escola ou ano não selecionado.'); setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.DESPESA}/all`, {
      params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        setItems(r.data);
      })
      .catch(err => {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar despesas.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => {
      if (name === 'idPrograma') {
        const progId = value ? parseInt(value) : '';
        return { ...f, idPrograma: progId, idAcao: '', idConta: '' };
      }
      // Converter para inteiro se for idPrograma, idAcao, idTipoBem (que são IDs numéricos)
      if (['idPrograma', 'idAcao', 'idTipoBem'].includes(name)) {
        return { ...f, [name]: value ? parseInt(value) : '' };
      }
      return { ...f, [name]: value };
    });
  }
  function handleNew() { setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', _isEditing: false }); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; const s = selected[0]; setForm({ ...s, docData: toIsoDate(s.docData), pgData: toIsoDate(s.pgData), _isEditing: true }); setShowModal(true); }
  function toIsoDocData(docData) {
    if (!docData) return '';
    if (Array.isArray(docData)) {
      const [y, m, d] = docData;
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    if (docData instanceof Date) return docData.toISOString().slice(0, 10);
    if (typeof docData === 'string') {
      if (/^\d{4},\d{1,2},\d{1,2}$/.test(docData)) {
        const [y, m, d] = docData.split(',');
        return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      return docData.slice(0, 10);
    }
    return String(docData);
  }
  function handleOpenBens() {
    if (selected.length !== 1) return;
    const s = selected[0];
    navigate('/bens', { state: { fromDespesa: { ...s, docData: toIsoDocData(s.docData), docTipo: s.docTipo || '' } } });
  }

  function handleSave(e, formToSend) {
    e.preventDefault();
    // Validar que idAcao foi selecionado
    if (!formToSend.idAcao) {
      showDefaultModal('error', 'Ação é obrigatória.');
      return;
    }
    axios.post(`${API_BASE_URL.DESPESA}/save`, formToSend, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar despesa.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.post(`${API_BASE_URL.DESPESA}/delete`, {
      ano: s.ano, idPrograma: s.idPrograma, idAcao: s.idAcao, idEscola: s.idEscola, idConta: s.idConta,
      idFavorecido: s.idFavorecido, idNatureza: s.idNatureza, idTipoBem: s.idTipoBem, docNum: s.docNum, docData: s.docData
    }, { headers: { Authorization: `Bearer ${token}` } })))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir despesa.'))
    );
  }

  function handleTransferir() {
    if (selected.length !== 1) return;
    setTransferirTarget(selected[0]);
    setShowTransferirModal(true);
  }

  function handleConfirmTransferir(novaConta) {
    if (!transferirTarget) return;
    axios.post(`${API_BASE_URL.DESPESA}/transferir`, {
      ano: transferirTarget.ano,
      idPrograma: transferirTarget.idPrograma,
      idAcao: transferirTarget.idAcao,
      idEscola: transferirTarget.idEscola,
      idContaOrigem: transferirTarget.idConta,
      novaConta,
      idFavorecido: transferirTarget.idFavorecido,
      idNatureza: transferirTarget.idNatureza,
      idTipoBem: transferirTarget.idTipoBem != null ? transferirTarget.idTipoBem : 0,
      docNum: transferirTarget.docNum,
      docData: toIsoDocData(transferirTarget.docData),
    }, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowTransferirModal(false);
        setTransferirTarget(null);
        setSelected([]);
        setAtualizar(a => !a);
        showDefaultModal('success', `Despesa transferida para a conta ${novaConta}.`);
      })
      .catch(err => {
        const msg = (err.response && err.response.data) || 'Erro ao transferir despesa.';
        showDefaultModal('error', msg);
      });
  }

  function normalizeDate(docData) {
    if (!docData) return '';
    // Se for Date
    if (docData instanceof Date) {
      return docData.toISOString().slice(0, 10);
    }
    // Se for array [ano, mes, dia]
    if (Array.isArray(docData)) {
      const [y, m, d] = docData;
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    // Se for string no formato 'YYYY-MM-DD' ou 'YYYY-M-D'
    if (typeof docData === 'string') {
      const match = docData.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (match) {
        const y = match[1];
        const m = String(match[2]).padStart(2, '0');
        const d = String(match[3]).padStart(2, '0');
        return `${y}-${m}-${d}`;
      }
      // Se for 'YYYY,M,D'
      if (/^\d{4},\d{1,2},\d{1,2}$/.test(docData)) {
        const [y, m, d] = docData.split(',');
        return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
      // Se for string longa, tenta pegar os 10 primeiros caracteres
      if (docData.length >= 10) return docData.slice(0, 10);
    }
    return String(docData);
  }
  function keyOf(item) {
    return `${item.ano}_${item.idPrograma}_${item.idAcao}_${item.idEscola}_${item.idConta}_${item.idFavorecido}_${item.idNatureza}_${item.idTipoBem != null ? item.idTipoBem : 0}_${item.docNum}_${normalizeDate(item.docData)}`;
  }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  function handleResumo() {
    if (selected.length !== 1) return;
    const s = selected[0];
    axios.post(`${API_BASE_URL.DESPESA}/preencherResumoDespesa`, s, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'CAPA_DESPESA.docx');
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => showDefaultModal('error', 'Erro ao gerar resumo da despesa.'));
  }
  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="DESPESAS" icon="bi-receipt" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
            <button className="btn btn-info btn-sm botao-igual" onClick={handleOpenBens} disabled={selected.length !== 1 || !isCapital(selected[0])} title="Registrar Bem para Despesa de Capital"><i className="bi bi-box-seam me-1"></i>Bens</button>
            <button className="btn btn-secondary btn-sm botao-igual" onClick={handleResumo} disabled={selected.length !== 1} title="Gerar Capa Resumo da Despesa"><i className="bi bi-file-earmark-text me-1"></i>Resumo</button>
            <button className="btn btn-primary btn-sm botao-igual centralizar-se-impar" onClick={() => setShowExtratoPicker(true)}><i className="bi bi-bank me-1"></i>Extratos</button>
            {isMaster && <button className="btn btn-warning btn-sm botao-igual" onClick={handleTransferir} disabled={selected.length !== 1} title="Transferir despesa para outra conta"><i className="bi bi-arrow-left-right me-1"></i>Transferir</button>}
            <div className="toolbar-search">
              <select className="form-select form-select-sm" value={filtroPrograma} onChange={e => setFiltroPrograma(e.target.value)}>
                <option value="">Todos os programas</option>
                {(programas || []).map(p => <option key={p.id} value={p.id}>{p.descricao}</option>)}
              </select>
            </div>

          </Toolbar>

          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={String(prog.idPrograma)} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={programas.find(p => p.id === prog.idPrograma)?.descricao || prog.idPrograma}>{programas.find(p => p.id === prog.idPrograma)?.descricao || prog.idPrograma}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Custeio ({prog.qtdC})</span><span>{formatCurrency(prog.custeio)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Capital ({prog.qtdK})</span><span>{formatCurrency(prog.capital)}</span>
                    </div>
                    <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{formatCurrency(prog.custeio + prog.capital)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <TableContainer isLoading={false} style={{ flex: '1 1 auto', minWidth: 0 }}>
                <DataTable style={{ fontSize: '0.75rem' }} head={
                <tr>
                          <th style={{ width: '3rem' }}>#</th>
                          <th>Programa / Ação</th>
                          <th>Favorecido</th>
                          <th>Natureza</th>
                          <th>Doc Nº</th>
                          <th>Doc Data</th>
                          <th className='text-center col-valor'>Valor</th>
                </tr>}>
                <tbody>
                  {displayed.map((item, idx) => {
                    const chave = keyOf(item);
                    const statusInfo = statusBensPorDespesa[chave];
                    if (isCapital(item)) {
                      console.log('Despesa:', item);
                      console.log('Chave:', chave);
                      console.log('Status info:', statusInfo);
                      console.log('statusBensPorDespesa:', statusBensPorDespesa);
                    }
                    let warning = false;
                    if (isCapital(item)) {
                      warning = statusInfo && statusInfo.status !== 'ok';
                    }
                    return (
                      <tr key={chave} className={selected.find(s => keyOf(s) === chave) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                        <td>{setPagination.startIndex + idx + 1}</td>
                        <td>{progAcaoLabel(item)}</td>
                        <td>{favorecidoLabel(item.idFavorecido)}</td>
                        <td>{naturezaLabel(item)}</td>
                        <td>{item.docNum}</td>
                        <td>{formatDateView(item.docData)}</td>
                        <td className='text-end col-valor'>
                            {formatCurrency(item.vlr)}{' '}
                          {warning && <i className="bi bi-exclamation-triangle-fill text-warning" title="Bens não cadastrados ou valor insuficiente"></i>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 20, background: '#fff', backgroundColor: '#fff', pointerEvents: 'auto', boxShadow: '0 -1px 0 rgba(0,0,0,0.04)' }}>
                  <tr style={{ background: '#fff', height: '2px' }}>
                    <td colSpan={7} style={{ background: '#fff', padding: 0, margin: 0, border: 'none', height: '2px', lineHeight: '2px' }} />
                  </tr>
                  <tr style={{ background: '#fff', height: '28px' }}>
                    {totalExtratos !== null ? (
                      <>
                        <td colSpan={3} style={{ textAlign: 'left', background: '#fff', position: 'relative', zIndex: 21, border: 'none' }}>
                          {Math.abs(totalExtratos - totalDespesas) < 0.01 ? (
                            <span className="fw-bold" style={{ color: '#198754', background: '#e9fbe7', padding: '4px 8px', borderRadius: '6px' }}>
                              <i className="bi bi-check-circle-fill me-2" aria-label="Conferência OK"></i>
                              Os valores de despesas e extratos conferem.
                            </span>
                          ) : (
                            <span className="fw-bold" style={{ color: '#b30000', background: '#ffeaea', padding: '4px 8px', borderRadius: '6px' }}>
                              <i className="bi bi-exclamation-triangle-fill me-2" aria-label="Atenção"></i>
                              Atenção: Diferença de {formatCurrency(Math.abs(totalExtratos - totalDespesas))} entre despesas e extratos.
                            </span>
                          )}
                        </td>
                        <td className="text-end fw-bold" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none', whiteSpace: 'nowrap' }}>Total dos extratos:</td>
                        <td className="text-end fw-bold col-valor" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none' }}>{formatCurrency(totalExtratos)}</td>
                        <td className="text-end fw-bold" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none', whiteSpace: 'nowrap' }}>Total das despesas:</td>
                        <td className="text-end fw-bold col-valor" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none' }}>{formatCurrency(totalDespesas)}</td>
                      </>
                    ) : (
                      <td colSpan={7} className="text-end fw-bold" style={{ background: '#fff', position: 'relative', zIndex: 21, border: 'none' }}>Total das despesas: {formatCurrency(totalDespesas)}</td>
                    )}
                  </tr>
                </tfoot>
              </DataTable>
            </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <DespesaModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange}
        programas={programas} acoes={acoes} contas={contas} favorecidos={favorecidos} naturezas={naturezas} tiposBens={tiposBens} />
      <ExtratoPicker show={showExtratoPicker} onClose={() => setShowExtratoPicker(false)} extratos={extratos} programas={programas} contas={contas} />
      <TransferirContaModal
        show={showTransferirModal}
        onClose={() => setShowTransferirModal(false)}
        onConfirm={handleConfirmTransferir}
        origem={transferirTarget ? transferirTarget.idConta : ''}
        idPrograma={transferirTarget ? transferirTarget.idPrograma : null}
      />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Despesas;
